import { Injectable, Inject, PLATFORM_ID } from '@angular/core'; 
import { isPlatformBrowser } from '@angular/common';              
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

import { UserSystem } from '../User/models/user-system'; 

// 🚀 INYECTADO: Importamos tu contrato máster relacional centralizado de forma limpia
import { AuthResponse } from '../models/auth-response'; // Ajusta la ruta exacta según tus carpetas

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiAuth: string = '/api/auth/login';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  login(companyCode: string, userId: string, password: string): Observable<AuthResponse> {
    console.log('✏️ [LOG AUTH_SERVICE] 1. Disparando petición POST Multi-Tenant para:', userId);
    
    // =========================================================================
    // 🚀 ARQUITECTURA 100% DINÁMICA: CERO CÓDIGO EN DURO (HARDCODED)
    // El payload recopila los tres campos vivos del formulario reactivo.
    // Esto te permitirá loguearte hoy en '00001' y mañana en cualquier taller nuevo.
    // =========================================================================
    const loginPayload = {
      coId: companyCode, // 💡 Captura dinámicamente el código escrito (ej. 'SYSTEM-TENANT-UUID-00000000000000000' o el UUID que corresponda)
      userId: userId,
      password: password
    };

    return this.http.post<AuthResponse>(this.apiAuth, loginPayload).pipe(
      tap(res => {
        // 🔒 BLINDAJE SSR: Solo escribe si estamos en el navegador
        if (isPlatformBrowser(this.platformId) && res && res.userId) {
          console.group('💾 [LOG AUTH_SERVICE] 2. Escribiendo en LocalStorage Multi-Tenant');
          console.log('Payload crudo Multi-Tenant a guardar:', res);
          
          // Guarda el JSON completo incluyendo el nuevo 'coId' de forma transparente
          localStorage.setItem('currentUser', JSON.stringify(res));
          
          console.log('Verificación inmediata post-guardado:', localStorage.getItem('currentUser'));
          console.groupEnd();
        } else {
          console.warn('⚠️ [LOG AUTH_SERVICE] No se guardó en LocalStorage. ¿Es navegador?:', isPlatformBrowser(this.platformId));
        }
      })
    );
  }

  /**
   * 💡 LA COMPUERTA AUTOMÁTICA DEL CONTEXTO:
   * Extrae de forma ultra segura el UUID criptográfico de la compañía desde el LocalStorage.
   * Servirá como el proveedor máster para el Interceptor HTTP del Front-end.
   */
  getCompanyId(): string {
    if (isPlatformBrowser(this.platformId)) {
      const userStr = localStorage.getItem('currentUser');
      if (!userStr) return '';
      const user: AuthResponse = JSON.parse(userStr);
      return user.coId || '';
    }
    return '';
  }

  getRoles(): string[] {
    //console.group('🔍 [LOG AUTH_SERVICE] 3. Ejecutando getRoles()');
    //console.log('¿Estamos parados en el Navegador?:', isPlatformBrowser(this.platformId));
    
    if (isPlatformBrowser(this.platformId)) {
      const userStr = localStorage.getItem('currentUser');
      //console.log('Contenido recuperado de la memoria (currentUser):', userStr);
      
      if (!userStr) {
        //console.warn('❌ getRoles() determinó que la memoria está VACÍA (null)');
        //console.groupEnd();
        return [];
      }
      
      const user: AuthResponse = JSON.parse(userStr);
      //console.log('Roles parseados con éxito:', user.roles);
      //console.groupEnd();
      return user.roles || [];
    }
    
    //onsole.log('🖥️ Ejecutando en Node.js (Servidor), se devuelve vacío preventivo.');
    //console.groupEnd();
    return [];
  }

  getAn8Asesor(): number {
    if (isPlatformBrowser(this.platformId)) {
      // Nota técnica: En el nuevo DTO del login de Java de tres pasos mapeaste la variable del AN8 del usuario 
      // si viaja dentro de la respuesta como 'an8', se conserva perfectamente en el cast del AuthResponse.
      const userStr = localStorage.getItem('currentUser');
      if (!userStr) return 0;
      const user: any = JSON.parse(userStr);
      return user.an8 || 0;
    }
    return 0;
  }

  hasRole(roleName: string): boolean {
    return this.getRoles().includes(roleName);
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('currentUser');
      console.log('>>> [SECURITY JDE] Sesión destruida de forma segura. LocalStorage limpio.');
    }
  }

  getUserId(): string {
    if (isPlatformBrowser(this.platformId)) {
      const userStr = localStorage.getItem('currentUser');
      if (!userStr) return 'Invitado'; 
      
      const user: AuthResponse = JSON.parse(userStr);
      return user.userId || 'Invitado';
    }
    return ''; 
  }

  // 🛠️ CORREGIDO: Ahora devuelve un arreglo de la interfaz real UserSystem
  getAllUsuariosMaster(): Observable<UserSystem[]> {
    return this.http.get<UserSystem[]>(`/api/auth/users`);
  }
 
  registrarUsuarioServidor(payload: any): Observable<any> {
    // =========================================================================
    // 🚀 EXTRACCIÓN DINÁMICA ULTRA SEGURA (SANA EL ERROR TS(2339))
    // SOLUCIÓN: Si la sesión parpadea en modales, extraemos de forma inteligente 
    // el coId que el propio formulario ya empaquetó en el payload (o fallbacks basales).
    // Esto garantiza un string lleno en 'coIdFallback=' inmune al SSR.
    // =========================================================================
    const coIdSeguro = payload?.coId || sessionStorage.getItem('coId') || localStorage.getItem('coId') || '';

    console.log('📡 [JDE RED] Despachando POST de registro con Coordenada SaaS Dinámica:', coIdSeguro);

    // Apuntamos a la URL unificada idéntica a tu AuthController de Spring Boot
    return this.http.post<any>(`/api/auth/users/register?coIdFallback=${coIdSeguro}`, payload);
  }


  getAsesoresActivos(): Observable<any[]> {
    // El interceptor le pegará automáticamente las cabeceras x-company-id para aislar los datos
    return this.http.get<any[]>(`/api/auth/asesores`);
  }  

  // Opcional: Si necesitas extraer el token guardado para Spring Security
  getToken(): string {
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem('currentUser');
      if (!userStr) return '';
      const user = JSON.parse(userStr);
      return user.token || '';
    }
    return '';
  }  

  // =========================================================================
  // 🛡️ VERIFICACIÓN DE SESIÓN ACTIVA (Sana el error ngtsc(2339))
  // =========================================================================
  isAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const userStr = localStorage.getItem('currentUser');
      if (!userStr) return false;
      
      try {
        const user: AuthResponse = JSON.parse(userStr);
        // Si existe el objeto y tiene un userId válido, asumimos que está autenticado
        return !!(user && user.userId);
      } catch (e) {
        return false;
      }
    }
    return false;
  }  

}